import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { 
  FileText, BookOpen, Target, Users, Scale, Lightbulb, 
  CheckCircle, XCircle, ArrowRight, Save, Download, 
  Loader2, Copy, ChevronDown, ChevronUp, Bookmark,
  Clock, Tag, AlertTriangle, HelpCircle, Newspaper,
  Upload, Image, FileUp, Lock, Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

import { getApiBaseUrl } from '../services/api';
const API_URL = getApiBaseUrl();

// Freemium: Free users get 2 analyses per day
const FREE_DAILY_LIMIT = 2;

const EditorialAnalyzerPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  // Freemium state
  const isPro = user?.role === "PRO";
  const [dailyUsage, setDailyUsage] = useState(0);
  
  // Form state
  const [title, setTitle] = useState('');
  const [source, setSource] = useState('The Hindu');
  const [editorialText, setEditorialText] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Analysis state
  const [analysis, setAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  
  // MCQ state
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showExplanations, setShowExplanations] = useState({});
  
  // Saved editorials
  const [savedEditorials, setSavedEditorials] = useState([]);
  const [showLibrary, setShowLibrary] = useState(false);
  
  // File upload state
  const [isExtracting, setIsExtracting] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (!user || !token) {
      navigate('/login');
    }
    // Load daily usage from localStorage
    loadDailyUsage();
  }, [user, token, navigate]);
  
  const loadDailyUsage = () => {
    const today = new Date().toISOString().split('T')[0];
    const stored = localStorage.getItem('editorial_daily_usage');
    if (stored) {
      const { date: storedDate, count } = JSON.parse(stored);
      if (storedDate === today) {
        setDailyUsage(count);
      } else {
        // Reset for new day
        localStorage.setItem('editorial_daily_usage', JSON.stringify({ date: today, count: 0 }));
        setDailyUsage(0);
      }
    }
  };
  
  const incrementDailyUsage = () => {
    const today = new Date().toISOString().split('T')[0];
    const newCount = dailyUsage + 1;
    localStorage.setItem('editorial_daily_usage', JSON.stringify({ date: today, count: newCount }));
    setDailyUsage(newCount);
  };
  
  const canAnalyze = isPro || dailyUsage < FREE_DAILY_LIMIT;
  const remainingAnalyses = isPro ? '∞' : Math.max(0, FREE_DAILY_LIMIT - dailyUsage);

  const analyzeEditorial = async () => {
    if (!title.trim() || !editorialText.trim()) {
      toast.error('Please enter both title and editorial text');
      return;
    }
    
    if (editorialText.trim().split(/\s+/).length < 100) {
      toast.error('Editorial text should be at least 100 words');
      return;
    }
    
    // Freemium check
    if (!canAnalyze) {
      toast.error(`Daily limit reached! Upgrade to PRO for unlimited analyses.`, {
        action: {
          label: 'Upgrade',
          onClick: () => navigate('/upgrade')
        }
      });
      return;
    }
    
    setIsAnalyzing(true);
    setAnalysis(null);
    
    try {
      const response = await fetch(`${API_URL}/api/editorial/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: title.trim(),
          source: source.trim(),
          editorial_text: editorialText.trim(),
          date: date
        })
      });
      
      if (!response.ok) {
        throw new Error('Analysis failed');
      }
      
      const data = await response.json();
      setAnalysis(data);
      setActiveTab('summary');
      
      // Increment usage for free users
      if (!isPro) {
        incrementDailyUsage();
      }
      
      toast.success('Editorial analyzed successfully!');
      
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Failed to analyze editorial. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const saveEditorial = async () => {
    if (!analysis) return;
    
    setIsSaving(true);
    
    try {
      const response = await fetch(`${API_URL}/api/editorial/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(analysis)
      });
      
      if (!response.ok) {
        throw new Error('Save failed');
      }
      
      toast.success('Editorial saved to library!');
      
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save editorial');
    } finally {
      setIsSaving(false);
    }
  };

  // File upload handler
  const handleFileUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a PDF or image file (JPG, PNG, WEBP)');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setIsExtracting(true);
    setUploadedFile(file);
    
    // Create an AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000); // 90 second timeout
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/editorial/extract-text`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const data = await response.json();
      
      // Debug: Log the response to console
      console.log('OCR Response:', data);
      
      if (!response.ok) {
        throw new Error(data.detail || 'Extraction failed');
      }
      
      // Check if extracted_text exists and has content
      const extractedText = data.extracted_text || '';
      
      if (extractedText.trim().length > 0) {
        // Set the editorial text
        setEditorialText(extractedText);
        
        // Try to auto-fill title from first line if title is empty
        if (!title.trim()) {
          const firstLine = extractedText.split('\n')[0].trim();
          if (firstLine && firstLine.length > 10 && firstLine.length < 200) {
            setTitle(firstLine);
          }
        }
        
        toast.success(`Text extracted from ${file.name} (${data.word_count || extractedText.split(/\s+/).length} words)`);
      } else {
        // If extraction returned empty, show error
        console.error('Empty extraction result:', data);
        toast.error('Could not extract text from the image. Please try with a clearer image or paste the text manually.');
        setUploadedFile(null);
      }
      
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Extraction error:', error);
      
      if (error.name === 'AbortError') {
        toast.error('Text extraction timed out. Please try with a smaller/clearer image or paste the text manually.');
      } else {
        toast.error(error.message || 'Failed to extract text from file. Please try again or paste the text manually.');
      }
      setUploadedFile(null);
    } finally {
      setIsExtracting(false);
    }
  };
  
  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };
  
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const exportToPDF = () => {
    if (!analysis) return;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${analysis.title} - UPSC Analysis | CLARIAS</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Georgia', serif; 
            line-height: 1.6; 
            color: #1a1a1a;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
          }
          .header { 
            border-bottom: 3px solid #f97316; 
            padding-bottom: 20px; 
            margin-bottom: 30px; 
          }
          .logo { 
            font-size: 14px; 
            color: #f97316; 
            font-weight: bold;
            margin-bottom: 10px;
          }
          h1 { 
            font-size: 24px; 
            color: #1e293b; 
            margin-bottom: 8px;
          }
          .meta { 
            font-size: 12px; 
            color: #64748b; 
          }
          .tags { 
            margin-top: 12px; 
          }
          .tag { 
            display: inline-block;
            background: #f1f5f9;
            padding: 4px 12px;
            border-radius: 4px;
            font-size: 11px;
            margin-right: 8px;
            margin-bottom: 6px;
            color: #475569;
          }
          .tag.gs { background: #dbeafe; color: #1d4ed8; }
          .tag.high { background: #dcfce7; color: #166534; }
          .section { 
            margin-bottom: 28px; 
            page-break-inside: avoid;
          }
          .section-title { 
            font-size: 16px; 
            color: #f97316; 
            border-left: 4px solid #f97316;
            padding-left: 12px;
            margin-bottom: 12px;
            font-weight: bold;
          }
          .content { 
            font-size: 13px; 
            color: #374151;
          }
          ul { 
            margin-left: 20px; 
          }
          li { 
            margin-bottom: 8px; 
          }
          .two-col { 
            display: flex; 
            gap: 30px; 
          }
          .col { flex: 1; }
          .pros li::marker { color: #22c55e; }
          .cons li::marker { color: #ef4444; }
          .mcq { 
            background: #f8fafc; 
            padding: 16px; 
            border-radius: 8px; 
            margin-bottom: 16px;
            border: 1px solid #e2e8f0;
          }
          .mcq-q { 
            font-weight: 600; 
            margin-bottom: 10px;
            font-size: 13px;
          }
          .mcq-opt { 
            font-size: 12px;
            margin-left: 16px;
            margin-bottom: 4px;
          }
          .mcq-opt.correct { 
            background: #dcfce7;
            padding: 4px 8px;
            border-radius: 4px;
          }
          .mcq-exp { 
            font-size: 11px; 
            color: #64748b; 
            margin-top: 8px;
            padding-top: 8px;
            border-top: 1px dashed #e2e8f0;
          }
          .answer-framework {
            background: #fef3c7;
            padding: 20px;
            border-radius: 8px;
            border: 1px solid #fcd34d;
          }
          .framework-section {
            margin-bottom: 12px;
          }
          .framework-label {
            font-size: 11px;
            color: #92400e;
            text-transform: uppercase;
            font-weight: bold;
            margin-bottom: 4px;
          }
          .footer { 
            margin-top: 40px; 
            padding-top: 20px; 
            border-top: 1px solid #e2e8f0;
            font-size: 10px; 
            color: #94a3b8; 
            text-align: center;
          }
          @media print {
            body { padding: 20px; }
            .section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">CLARIAS - UPSC Editorial Analysis</div>
          <h1>${analysis.title}</h1>
          <div class="meta">${analysis.source} | ${analysis.date} | ${analysis.word_count} words</div>
          <div class="tags">
            ${analysis.gs_papers.map(gs => `<span class="tag gs">${gs}</span>`).join('')}
            <span class="tag high">Mains: ${analysis.mains_relevance}</span>
            <span class="tag">Prelims: ${analysis.prelims_relevance}</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">📝 Summary (100 words)</div>
          <div class="content">${analysis.summary_100}</div>
        </div>

        <div class="section">
          <div class="section-title">📋 Detailed Summary (250 words)</div>
          <div class="content">${analysis.summary_250}</div>
        </div>

        <div class="section">
          <div class="section-title">📌 Key Facts</div>
          <ul class="content">
            ${analysis.key_facts.map(f => `<li>${f}</li>`).join('')}
          </ul>
        </div>

        <div class="section">
          <div class="section-title">⚠️ Problem Statement</div>
          <div class="content">${analysis.problem_statement}</div>
        </div>

        <div class="section">
          <div class="section-title">👥 Stakeholder Analysis</div>
          <ul class="content">
            ${analysis.stakeholders.map(s => `<li><strong>${s.name}</strong> (${s.role}): ${s.impact}</li>`).join('')}
          </ul>
        </div>

        <div class="section">
          <div class="two-col">
            <div class="col">
              <div class="section-title">✅ Pros / Advantages</div>
              <ul class="content pros">
                ${analysis.pros.map(p => `<li>${p}</li>`).join('')}
              </ul>
            </div>
            <div class="col">
              <div class="section-title">❌ Cons / Challenges</div>
              <ul class="content cons">
                ${analysis.cons.map(c => `<li>${c}</li>`).join('')}
              </ul>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">💡 Way Forward</div>
          <ul class="content">
            ${analysis.way_forward.map(w => `<li>${w}</li>`).join('')}
          </ul>
        </div>

        <div class="section">
          <div class="section-title">📚 Mains Question</div>
          <div class="content" style="font-style: italic; background: #f1f5f9; padding: 12px; border-radius: 6px;">
            ${analysis.mains_question}
          </div>
        </div>

        <div class="section">
          <div class="section-title">📝 Answer Framework</div>
          <div class="answer-framework">
            <div class="framework-section">
              <div class="framework-label">Introduction</div>
              <div>${analysis.answer_framework.intro}</div>
            </div>
            <div class="framework-section">
              <div class="framework-label">Body Points</div>
              <ol style="margin-left: 20px;">
                ${analysis.answer_framework.body_points.map(p => `<li>${p}</li>`).join('')}
              </ol>
            </div>
            <div class="framework-section">
              <div class="framework-label">Conclusion</div>
              <div>${analysis.answer_framework.conclusion}</div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">📖 Prelims MCQs</div>
          ${analysis.mcqs.map((mcq, i) => `
            <div class="mcq">
              <div class="mcq-q">Q${i + 1}. ${mcq.question}</div>
              ${mcq.options.map((opt, j) => `
                <div class="mcq-opt ${j === mcq.correct_index ? 'correct' : ''}">
                  ${String.fromCharCode(65 + j)}. ${opt} ${j === mcq.correct_index ? '✓' : ''}
                </div>
              `).join('')}
              <div class="mcq-exp"><strong>Explanation:</strong> ${mcq.explanation}</div>
            </div>
          `).join('')}
        </div>

        <div class="section">
          <div class="section-title">🔗 Linkages</div>
          <div class="content">
            ${analysis.constitutional_articles.length > 0 ? `<p><strong>Constitutional Articles:</strong> ${analysis.constitutional_articles.join(', ')}</p>` : ''}
            ${analysis.acts_laws.length > 0 ? `<p><strong>Acts & Laws:</strong> ${analysis.acts_laws.join(', ')}</p>` : ''}
            ${analysis.government_schemes.length > 0 ? `<p><strong>Government Schemes:</strong> ${analysis.government_schemes.join(', ')}</p>` : ''}
            ${analysis.international_conventions.length > 0 ? `<p><strong>International Conventions:</strong> ${analysis.international_conventions.join(', ')}</p>` : ''}
          </div>
        </div>

        <div class="section">
          <div class="section-title">🏷️ Keywords for Revision</div>
          <div class="tags">
            ${analysis.keywords.map(k => `<span class="tag">${k}</span>`).join('')}
          </div>
        </div>

        <div class="footer">
          Generated by CLARIAS - Your AI-Powered UPSC Preparation Partner | clarias.in
          <br/>Analyzed on: ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
      </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.print();
    };
    
    toast.success('PDF export ready! Use "Save as PDF" in print dialog.');
  };

  const handleMCQSelect = (mcqIndex, optionIndex) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [mcqIndex]: optionIndex
    });
  };

  const toggleExplanation = (mcqIndex) => {
    setShowExplanations({
      ...showExplanations,
      [mcqIndex]: !showExplanations[mcqIndex]
    });
  };

  const getRelevanceBadgeColor = (relevance) => {
    switch (relevance) {
      case 'HIGH': return 'bg-green-500';
      case 'MEDIUM': return 'bg-yellow-500';
      case 'LOW': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50/30 to-amber-50/30">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl">
              <Newspaper className="h-8 w-8 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                Editorial Analyzer
              </h1>
              <p className="text-slate-600">
                Paste any editorial • Get complete UPSC analysis
              </p>
            </div>
            {/* Freemium Usage Badge */}
            <div className="flex items-center gap-2">
              <Badge className={`px-3 py-1.5 ${isPro ? 'bg-emerald-500/20 text-emerald-700 border-emerald-500/30' : 'bg-amber-500/20 text-amber-700 border-amber-500/30'}`}>
                {isPro ? <Crown className="h-3 w-3 mr-1" /> : <FileText className="h-3 w-3 mr-1" />}
                {isPro ? 'PRO' : 'FREE'}
              </Badge>
              <div className="text-right">
                <p className="text-xs text-slate-500">Today&apos;s Analyses</p>
                <p className={`text-lg font-bold ${isPro ? 'text-emerald-600' : remainingAnalyses === 0 ? 'text-red-500' : 'text-amber-600'}`}>
                  {remainingAnalyses}{!isPro && `/${FREE_DAILY_LIMIT}`}
                </p>
              </div>
            </div>
          </div>
          
          {/* Freemium Upgrade Banner */}
          {!isPro && remainingAnalyses === 0 && (
            <div className="mt-4 bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-300 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Lock className="h-5 w-5 text-amber-600" />
                <div className="flex-1">
                  <p className="text-amber-800 font-medium">Daily limit reached!</p>
                  <p className="text-amber-700 text-sm">Upgrade to PRO for unlimited editorial analyses</p>
                </div>
                <Link to="/upgrade">
                  <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                    <Crown className="h-4 w-4 mr-2" />
                    Upgrade
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left: Input Form */}
          <div className="space-y-4">
            <Card className="bg-white/80 border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-slate-800">
                  <FileText className="h-5 w-5 text-orange-500" />
                  Paste Editorial
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Editorial Title *
                  </label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., India's Trade Policy Needs Overhaul"
                    className="bg-white border-slate-300 text-slate-800 placeholder:text-slate-600"
                    data-testid="editorial-title-input"
                  />
                </div>
                
                {/* File Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Or Upload Editorial <span className="text-slate-500">(Image/PDF)</span>
                  </label>
                  <div
                    className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer
                      ${dragActive 
                        ? 'border-orange-500 bg-orange-100' 
                        : 'border-slate-300 hover:border-orange-400 bg-slate-50'
                      }
                      ${isExtracting ? 'pointer-events-none opacity-60' : ''}
                    `}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('file-upload').click()}
                    data-testid="file-upload-zone"
                  >
                    <input
                      id="file-upload"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={handleFileInputChange}
                      className="hidden"
                      data-testid="file-upload-input"
                    />
                    
                    {isExtracting ? (
                      <div className="flex flex-col items-center">
                        <Loader2 className="h-10 w-10 text-orange-500 animate-spin mb-3" />
                        <p className="text-slate-700 font-medium">Extracting text...</p>
                        <p className="text-xs text-slate-500 mt-1">This may take 30-60 seconds for images</p>
                        <p className="text-xs text-orange-600 mt-2">Please wait, AI is reading your editorial...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Image className="h-6 w-6 text-blue-600" />
                          </div>
                          <div className="p-2 bg-red-100 rounded-lg">
                            <FileUp className="h-6 w-6 text-red-500" />
                          </div>
                        </div>
                        <p className="text-slate-700 font-medium">
                          {dragActive ? 'Drop file here' : 'Click or drag to upload'}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Supports: JPG, PNG, WEBP, PDF (max 10MB)
                        </p>
                        {uploadedFile && !isExtracting && editorialText.trim().length > 0 && (
                          <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Extracted from: {uploadedFile.name}
                          </p>
                        )}
                        {uploadedFile && !isExtracting && editorialText.trim().length === 0 && (
                          <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                            Extraction may have failed. Please paste text manually.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="flex-1 border-t border-slate-300"></div>
                  <span className="text-xs text-slate-500">OR paste text below</span>
                  <div className="flex-1 border-t border-slate-300"></div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Editorial Text * <span className="text-slate-500">(min 100 words)</span>
                  </label>
                  <Textarea
                    value={editorialText}
                    onChange={(e) => setEditorialText(e.target.value)}
                    placeholder="Paste the full editorial text here..."
                    className="bg-white border-slate-300 text-slate-800 min-h-[300px] placeholder:text-slate-600"
                    data-testid="editorial-text-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Word count: {editorialText.trim().split(/\s+/).filter(w => w).length}
                  </p>
                </div>
                
                <Button
                  onClick={analyzeEditorial}
                  disabled={isAnalyzing || !title.trim() || !editorialText.trim()}
                  className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
                  data-testid="analyze-editorial-btn"
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Analyzing... (30-60 sec)
                    </>
                  ) : (
                    <>
                      <Target className="mr-2 h-4 w-4" />
                      Analyze for UPSC
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
            
            {/* Tips Card */}
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <HelpCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div className="text-sm text-slate-600">
                    <p className="font-medium text-slate-700 mb-1">Tips:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Upload a photo or PDF of the editorial for automatic text extraction</li>
                      <li>Or copy editorials from The Hindu, Indian Express, or PIB</li>
                      <li>Include the full article for best analysis</li>
                      <li>Save important editorials to your library for revision</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Analysis Results */}
          <div>
            {!analysis && !isAnalyzing && (
              <Card className="bg-white/60 border-slate-200 h-full flex items-center justify-center">
                <CardContent className="text-center py-16">
                  <Newspaper className="h-16 w-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-600 text-lg">
                    Paste an editorial and click &quot;Analyze for UPSC&quot;
                  </p>
                  <p className="text-slate-500 text-sm mt-2">
                    Get summaries, MCQs, answer frameworks & more
                  </p>
                </CardContent>
              </Card>
            )}

            {isAnalyzing && (
              <Card className="bg-white/60 border-slate-200 h-full flex items-center justify-center">
                <CardContent className="text-center py-16">
                  <Loader2 className="h-16 w-16 text-orange-500 mx-auto mb-4 animate-spin" />
                  <p className="text-slate-700 text-lg">
                    Analyzing editorial...
                  </p>
                  <p className="text-slate-500 text-sm mt-2">
                    Generating summaries, MCQs, and answer frameworks
                  </p>
                </CardContent>
              </Card>
            )}

            {analysis && (
              <div className="space-y-4">
                {/* Analysis Header */}
                <Card className="bg-white/80 border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg text-slate-800">{analysis.title}</h3>
                        <p className="text-sm text-slate-500">
                          {analysis.source} • {analysis.date} • {analysis.word_count} words
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={saveEditorial}
                          disabled={isSaving}
                          className="border-slate-300"
                          data-testid="save-editorial-btn"
                          title="Save to Library"
                        >
                          {isSaving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Save className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={exportToPDF}
                          className="border-slate-300"
                          data-testid="export-pdf-btn"
                          title="Export as PDF"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {analysis.gs_papers.map((gs, i) => (
                        <Badge key={i} className="bg-blue-100 text-blue-600">
                          {gs}
                        </Badge>
                      ))}
                      <Badge className={`${getRelevanceBadgeColor(analysis.mains_relevance)} text-white`}>
                        Mains: {analysis.mains_relevance}
                      </Badge>
                      <Badge className={`${getRelevanceBadgeColor(analysis.prelims_relevance)} text-white`}>
                        Prelims: {analysis.prelims_relevance}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid grid-cols-5 bg-white/80">
                    <TabsTrigger value="summary" className="data-[state=active]:bg-orange-500">
                      Summary
                    </TabsTrigger>
                    <TabsTrigger value="analysis" className="data-[state=active]:bg-orange-500">
                      Analysis
                    </TabsTrigger>
                    <TabsTrigger value="mains" className="data-[state=active]:bg-orange-500">
                      Mains
                    </TabsTrigger>
                    <TabsTrigger value="mcqs" className="data-[state=active]:bg-orange-500">
                      MCQs
                    </TabsTrigger>
                    <TabsTrigger value="links" className="data-[state=active]:bg-orange-500">
                      Links
                    </TabsTrigger>
                  </TabsList>

                  {/* Summary Tab */}
                  <TabsContent value="summary" className="mt-4 space-y-4">
                    <Card className="bg-white/80 border-slate-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center justify-between">
                          <span>100-Word Summary</span>
                          <Button size="sm" variant="ghost" onClick={() => copyToClipboard(analysis.summary_100)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-700">{analysis.summary_100}</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-white/80 border-slate-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center justify-between">
                          <span>250-Word Summary</span>
                          <Button size="sm" variant="ghost" onClick={() => copyToClipboard(analysis.summary_250)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-700">{analysis.summary_250}</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-white/80 border-slate-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Key Facts</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {analysis.key_facts.map((fact, i) => (
                            <li key={i} className="flex items-start gap-2 text-slate-700">
                              <CheckCircle className="h-4 w-4 text-green-400 mt-1 flex-shrink-0" />
                              <span>{fact}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>

                    <Card className="bg-white/80 border-slate-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Keywords</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {analysis.keywords.map((kw, i) => (
                            <Badge key={i} variant="outline" className="border-slate-300 text-slate-700">
                              {kw}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Analysis Tab */}
                  <TabsContent value="analysis" className="mt-4 space-y-4">
                    <Card className="bg-white/80 border-slate-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-400" />
                          Problem Statement
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-700">{analysis.problem_statement}</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-white/80 border-slate-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Users className="h-4 w-4 text-blue-400" />
                          Stakeholder Analysis
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {analysis.stakeholders.map((s, i) => (
                            <div key={i} className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                              <p className="font-medium text-slate-800">{s.name}</p>
                              <p className="text-sm text-slate-600">Role: {s.role}</p>
                              <p className="text-sm text-slate-600">Impact: {s.impact}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    <div className="grid grid-cols-2 gap-4">
                      <Card className="bg-white/80 border-slate-200">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-400" />
                            Pros / Advantages
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {analysis.pros.map((pro, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                <span className="text-green-400">+</span>
                                <span>{pro}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>

                      <Card className="bg-white/80 border-slate-200">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-400" />
                            Cons / Challenges
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {analysis.cons.map((con, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                <span className="text-red-400">-</span>
                                <span>{con}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    </div>

                    <Card className="bg-white/80 border-slate-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Lightbulb className="h-4 w-4 text-yellow-400" />
                          Way Forward
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {analysis.way_forward.map((wf, i) => (
                            <li key={i} className="flex items-start gap-2 text-slate-700">
                              <ArrowRight className="h-4 w-4 text-yellow-400 mt-1 flex-shrink-0" />
                              <span>{wf}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Mains Tab */}
                  <TabsContent value="mains" className="mt-4 space-y-4">
                    <Card className="bg-white/80 border-slate-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <BookOpen className="h-4 w-4 text-purple-400" />
                            Mains Question
                          </span>
                          <Button size="sm" variant="ghost" onClick={() => copyToClipboard(analysis.mains_question)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-700 p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                          {analysis.mains_question}
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="bg-white/80 border-slate-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center justify-between">
                          <span>Answer Framework</span>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => copyToClipboard(
                              `Introduction:\n${analysis.answer_framework.intro}\n\nBody:\n${analysis.answer_framework.body_points.map((p, i) => `${i+1}. ${p}`).join('\n')}\n\nConclusion:\n${analysis.answer_framework.conclusion}`
                            )}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <p className="text-xs text-slate-500 uppercase mb-1 font-medium">Introduction</p>
                          <p className="text-slate-700 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            {analysis.answer_framework.intro}
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-xs text-slate-500 uppercase mb-1 font-medium">Body Points</p>
                          <ul className="space-y-2">
                            {analysis.answer_framework.body_points.map((point, i) => (
                              <li key={i} className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-slate-700">
                                <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded font-medium">
                                  {i + 1}
                                </span>
                                <span>{point}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div>
                          <p className="text-xs text-slate-500 uppercase mb-1 font-medium">Conclusion</p>
                          <p className="text-slate-700 p-3 bg-green-50 border border-green-200 rounded-lg">
                            {analysis.answer_framework.conclusion}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* MCQs Tab */}
                  <TabsContent value="mcqs" className="mt-4 space-y-4">
                    {analysis.mcqs.map((mcq, mcqIndex) => (
                      <Card key={mcqIndex} className="bg-white/80 border-slate-200">
                        <CardContent className="p-4">
                          <p className="font-medium text-slate-800 mb-3">
                            Q{mcqIndex + 1}. {mcq.question}
                          </p>
                          
                          <div className="space-y-2">
                            {mcq.options.map((option, optIndex) => {
                              const isSelected = selectedAnswers[mcqIndex] === optIndex;
                              const isCorrect = mcq.correct_index === optIndex;
                              const showResult = selectedAnswers[mcqIndex] !== undefined;
                              
                              let bgColor = 'bg-slate-50 hover:bg-slate-100 border-slate-200';
                              if (showResult) {
                                if (isCorrect) bgColor = 'bg-green-100 border-green-500';
                                else if (isSelected && !isCorrect) bgColor = 'bg-red-100 border-red-500';
                              } else if (isSelected) {
                                bgColor = 'bg-orange-100 border-orange-500';
                              }
                              
                              return (
                                <button
                                  key={optIndex}
                                  onClick={() => handleMCQSelect(mcqIndex, optIndex)}
                                  className={`w-full text-left p-3 rounded-lg border ${bgColor} text-slate-700 transition-colors`}
                                  disabled={showResult}
                                >
                                  <span className="font-medium mr-2">
                                    {String.fromCharCode(65 + optIndex)}.
                                  </span>
                                  {option}
                                </button>
                              );
                            })}
                          </div>
                          
                          {selectedAnswers[mcqIndex] !== undefined && (
                            <div className="mt-3">
                              <button
                                onClick={() => toggleExplanation(mcqIndex)}
                                className="text-sm text-orange-600 flex items-center gap-1"
                              >
                                {showExplanations[mcqIndex] ? (
                                  <>
                                    <ChevronUp className="h-4 w-4" />
                                    Hide Explanation
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="h-4 w-4" />
                                    Show Explanation
                                  </>
                                )}
                              </button>
                              
                              {showExplanations[mcqIndex] && (
                                <p className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-slate-700">
                                  {mcq.explanation}
                                </p>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </TabsContent>

                  {/* Links Tab */}
                  <TabsContent value="links" className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Card className="bg-white/80 border-slate-200">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">Constitutional Articles</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex flex-wrap gap-2">
                            {analysis.constitutional_articles.length > 0 ? (
                              analysis.constitutional_articles.map((art, i) => (
                                <Badge key={i} className="bg-blue-100 text-blue-600">
                                  {art}
                                </Badge>
                              ))
                            ) : (
                              <p className="text-slate-500 text-sm">Not applicable</p>
                            )}
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="bg-white/80 border-slate-200">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">Acts & Laws</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex flex-wrap gap-2">
                            {analysis.acts_laws.length > 0 ? (
                              analysis.acts_laws.map((law, i) => (
                                <Badge key={i} className="bg-purple-100 text-purple-600">
                                  {law}
                                </Badge>
                              ))
                            ) : (
                              <p className="text-slate-500 text-sm">Not applicable</p>
                            )}
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="bg-white/80 border-slate-200">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">Government Schemes</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex flex-wrap gap-2">
                            {analysis.government_schemes.length > 0 ? (
                              analysis.government_schemes.map((sch, i) => (
                                <Badge key={i} className="bg-green-100 text-green-600">
                                  {sch}
                                </Badge>
                              ))
                            ) : (
                              <p className="text-slate-500 text-sm">Not applicable</p>
                            )}
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="bg-white/80 border-slate-200">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">International Conventions</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex flex-wrap gap-2">
                            {analysis.international_conventions.length > 0 ? (
                              analysis.international_conventions.map((conv, i) => (
                                <Badge key={i} className="bg-amber-100 text-amber-700">
                                  {conv}
                                </Badge>
                              ))
                            ) : (
                              <p className="text-slate-500 text-sm">Not applicable</p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    <Card className="bg-white/80 border-slate-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Dimensions Covered</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {analysis.dimensions.map((dim, i) => (
                            <Badge key={i} variant="outline" className="border-orange-400 text-orange-600">
                              {dim}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-white/80 border-slate-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Topics Covered</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {analysis.topics_covered.map((topic, i) => (
                            <Badge key={i} variant="outline" className="border-slate-400 text-slate-700">
                              {topic}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditorialAnalyzerPage;
